import { eq } from "drizzle-orm";
import { type PowerSyncDrizzleDB, user } from "@fieldcheck/powersync";

export interface UserPreferences {
  locale: string | null;
  global_theme_id: string | null;
  is_blur: number | null;
  follow_device_theme: number | null;
  font_family: string | null;
  /**
   * Clave de storage del avatar (`avatars/{userId}/{hash}.webp`).
   *
   * Va aquí y no se lee de la sesión a propósito: la sesión la sirve la API y
   * queda cacheada, así que una foto recién subida no se reflejaría hasta el
   * siguiente refresco. Leyéndola de la fila local —igual que el idioma, la
   * fuente y el tema— la foto aparece al instante y sobrevive offline.
   */
  image: string | null;
}

/**
 * Shared Drizzle query builder for User preferences.
 */
export function getUserPreferencesQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      locale: user.locale,
      global_theme_id: user.global_theme_id,
      is_blur: user.is_blur,
      follow_device_theme: user.follow_device_theme,
      font_family: user.font_family,
      image: user.image,
    })
    .from(user)
    .where(eq(user.id, userId))
    .limit(1);
}

/**
 * Shared Drizzle query builder for User locale.
 */
export function getUserLocaleQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      locale: user.locale,
    })
    .from(user)
    .where(eq(user.id, userId))
    .limit(1);
}

/**
 * Shared Drizzle query builder for User font family preference.
 */
export function getUserFontFamilyQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      font_family: user.font_family,
    })
    .from(user)
    .where(eq(user.id, userId))
    .limit(1);
}

/**
 * Shared Drizzle query builder for User theme settings.
 *
 * Filtra SIEMPRE por `userId`, igual que el resto de lecturas de `user` de este
 * módulo. Antes decidía el filtro en una rama y el invitado se quedaba con
 * `SELECT … FROM user LIMIT 1` a secas: sin `WHERE`, SQLite devuelve la PRIMERA
 * fila que le venga bien, que no tiene por qué ser la del invitado. Eso no es un
 * fallo de corrección sino una fuga de datos entre cuentas: en el upgrade
 * invitado→real la base local baja una fila de `user` por cada co-miembro de la
 * organización, así que el invitado acababa pintando el tema de OTRA persona.
 * Sin `WHERE` no hay resultado vacío posible: siempre sale la fila de alguien.
 */
export function getUserThemeSettingsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      global_theme_id: user.global_theme_id,
      is_blur: user.is_blur,
      follow_device_theme: user.follow_device_theme,
    })
    .from(user)
    .where(eq(user.id, userId))
    .limit(1);
}
